import ng from 'angular';
import {
    AlertManagerService,
    ApiErrorModel,
    AuthContextService,
    CsrDecoderService,
    DateTimeWrapper,
    NavigationService,
    PriceCacheService,
    ProductHelperService,
    ProductsModelService,
    SslCertificateModelService
} from '@/services';
import { SslCertificateEditCallcenter } from './ssl-certificate-edit-callcenter';
import { UiRights } from '@/configuration';
import * as Types from '@/types';
import { ProductPriceParams } from '@/logic/product-price-new';

import './ssl-certificate-edit.scss';

export interface Certificate {
    brand: string;
    endDate: any;
    id: string;
    productCode: string;
    status: string;
    validationType: string;
    product?: string;
    isManaged?: boolean;
    renewPossible?: boolean;
}

export class OrganismEditFormSslCertificateController implements ng.IController {
    public static $inject: string[] = [
        '$translate',
        'alertManager',
        'apiErrorModel',
        'navigation',
        'productsModel',
        'sslCertificateModel',
        'csrDecoder',
        'priceCache',
    ];

    public certificate: Certificate;
    public enableCancelOrder = false;
    public panelRightSettings: any;
    public panelReissueRightSettings: any;
    public hasAgreedToReissueCertificate = false;
    public hasAgreedToRevokeCertificate = false;
    public reissueCsr = '';
    public showReissuePanel = false;
    public showObjectId: boolean;
    public renewable = false;
    public callcenter: SslCertificateEditCallcenter;
    private _cancelButtonClicked = false;

    constructor(
        private $translate: ng.translate.ITranslateService,
        private alertManager: AlertManagerService,
        private apiErrorModel: ApiErrorModel,
        public navigation: NavigationService,
        private productsModel: ProductsModelService,
        private sslCertificateModel: SslCertificateModelService,
        private csrDecoder: CsrDecoderService,
        private priceCache: PriceCacheService,
    ) {
        const requests = {
            decodeCsr: (csr: string): Promise<Types.SslApi.DecodeCsrResult> => {
                return this.csrDecoder.decode(csr);
            },
            renew: (id: string, csr: string) => {
                return this.sslCertificateModel.renewCertificate(id, csr);
            },
            handleRenewResponse: (result: any) => {
                if (!this.apiErrorModel.apiResponseHasError(result)) {
                    this.alertManager.success(this.$translate.instant('TR_110119-7ace6a_TR'));
                    return result;
                }
            },
            priceFromProductCode: (params: ProductPriceParams): Promise<any> => {
                return this.priceCache.getPriceByProductCode(params.productCode, params.accountId)
            }
        };

        this.callcenter = new SslCertificateEditCallcenter(requests);
    }

    public $onInit = (): void => {
        this.callcenter.certificate = this.certificate;
        const lang = this.$translate.use() as string;
        this.panelReissueRightSettings = ng.copy(this.panelRightSettings);
        this.renewable = this.certificate.renewPossible;
        if (
            this.certificate?.isManaged === true
            && !AuthContextService.isGranted(UiRights.ADMIN_SYSTEM_SUPER_USER_EDIT)
        ) {
            this.panelReissueRightSettings.reissuePanel = false;
        }
        void this.productsModel.findOne(this.certificate.productCode, lang)
        .then(
            (result) => {
                const maxReissues = ProductHelperService.wrapSpecificationItems(
                    result.response.specificationItems
                ).maxReissues?.intValue;

                if (maxReissues === undefined) {
                    return;
                }
                this.showReissuePanel = maxReissues > 0 || maxReissues === -1;
            }
        );
    };

    public get waitingForEmailValidation() {
        return this.certificate.status === 'creating' && this.certificate.validationType === 'validateViaEmail';
    }

    public get supportUrl() {
        if (this.certificate.brand === 'symantec') {
            if (this.certificate.product.indexOf('RapidSSL') === 0) {
                return 'https://www.rapidssl.com/support/contact/';
            } else {
                return 'https://www.geotrust.com/support/contact/';
            }
        }
        return '';
    }

    public get brandName() {
        if (this.certificate.brand === 'symantec') {
            if (this.certificate.product.indexOf('RapidSSL') === 0) {
                return 'RapidSSL';
            } else {
                return 'GeoTrust';
            }
        }
        return '';
    }

    public get showSupportLink() {
        return this.certificate.brand === 'symantec';
    }

    public get cancelButtonClicked(): boolean {
        return this._cancelButtonClicked;
    }

    public set cancelButtonClicked(clicked: boolean) {
        this._cancelButtonClicked = clicked;
    }

    public toggleCancelOrder = (enableCancelOrder: boolean) => {
        this.enableCancelOrder = !enableCancelOrder;
    }

    public certIsOutdated = () => {
        const now = new DateTimeWrapper();
        now.setToday();
        const certEnd = new DateTimeWrapper(this.certificate.endDate);
        return certEnd.toUnixTimestamp() < now.toUnixTimestamp();
    };

    public resendVerificationMail = () => {
        this.sslCertificateModel.resendApproverEmails([this.certificate]).then(
            () => {
                this.alertManager.success(this.$translate.instant('TR_110119-0fd78a_TR'));
            });
    };

    public cancelReissue = () => {
        this.reissueCsr = '';
        this.hasAgreedToRevokeCertificate = false;
    };

    public cancelRevoke = () => {
        this.hasAgreedToRevokeCertificate = false;
    };

    public reissueCertificate = () => {
        return this.sslCertificateModel.reissueCertificate(this.certificate.id, this.reissueCsr)
        .then(
            (res) => {
                if (!this.apiErrorModel.apiResponseHasError(res)) {
                    this.alertManager.success(this.$translate.instant('TR_110119-7ace6a_TR'));
                    return res;
                }

                return Promise.reject(false);
            }
        );
    };

    public renewCertificate = () => {
        this.callcenter.renewCertificate(this.certificate.id);
    }

    public revokeCertificate = () => {
        return this.sslCertificateModel.revoke([this.certificate]).then(
            (res) => {
                if (!this.apiErrorModel.apiResponseHasError(res)) {
                    this.alertManager.success(this.$translate.instant('TR_110119-d2d552_TR'));
                    return res;
                }

                return Promise.reject(false);
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    };

    public cancelRunningOrder = () => {
        this.cancelButtonClicked = true;
        return this.sslCertificateModel.cancelOrder(this.certificate.id).then(
            () => {
                this.navigation.go('ssl.dashboard');
                this.alertManager.success(this.$translate.instant('TR_100119-139c51_TR'));
            }
        )
    }
}

export class OrganismEditFormSslCertificateComponent implements ng.IComponentOptions {
    public bindings = {
        certificate: '<',
        panelRightSettings: '<',
        showObjectId: '<',
    };
    public controller = OrganismEditFormSslCertificateController;
    public controllerAs = '$editFormOrganismSslCertificate';
    public template = require('./ssl-certificate-edit.html');
}
